import { useTranslation } from 'react-i18next'

const InterfaceShowcase = () => {
  const { t } = useTranslation()

  // Technical folder names - not translated
  const projectFolders = ['frontend', 'rust-general', 'gui-tauri', 'composition-patterns', 'task-planning']

  // Metadata field labels - not translated (technical terms)
  const metadataFields = ['Metadata', 'Description', 'Category', 'Tags']

  return (
    <section id="interface" className="relative py-32 px-6 overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-crafter-surface/50 to-transparent pointer-events-none" />

      <div className="max-w-7xl mx-auto relative">
        {/* Section header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 text-crafter-accent font-mono text-sm mb-4">
            <span className="w-8 h-[1px] bg-crafter-accent/50" />
            <span>{t('interface.sectionLabel')}</span>
            <span className="w-8 h-[1px] bg-crafter-accent/50" />
          </div>
          <h2 className="font-sans font-bold text-4xl md:text-5xl text-crafter-text mb-6">
            {t('interface.title')}
          </h2>
          <p className="font-sans text-lg text-crafter-textDim max-w-2xl mx-auto">
            {t('interface.description')}
          </p>
        </div>

        {/* Interface mockup */}
        <div className="relative max-w-5xl mx-auto">
          {/* Window frame */}
          <div className="bg-crafter-base border border-crafter-accent/20 rounded-lg overflow-hidden shadow-2xl">
            {/* Title bar */}
            <div className="flex items-center gap-2 px-4 py-3 bg-crafter-surface border-b border-crafter-accent/10">
              <div className="flex gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500/80" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                <div className="w-3 h-3 rounded-full bg-green-500/80" />
              </div>
              <div className="flex-1 text-center">
                <span className="font-mono text-xs text-crafter-textDim">{t('interface.windowTitle')}</span>
              </div>
            </div>

            {/* Content */}
            <div className="grid grid-cols-12 divide-x divide-crafter-accent/10" style={{ height: '500px' }}>
              {/* Left panel - Project tree */}
              <div className="col-span-3 bg-crafter-surface/30 p-4">
                <div className="font-mono text-xs text-crafter-accent mb-4">{t('interface.projectTree')}</div>
                <div className="space-y-2">
                  {projectFolders.map((item, i) => (
                    <div
                      key={item}
                      className="flex items-center gap-2 px-3 py-2 rounded bg-crafter-elevated/50 text-sm text-crafter-textDim font-mono"
                      style={{ animationDelay: `${i * 0.1}s` }}
                    >
                      <svg className="w-4 h-4 text-crafter-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              {/* Center panel - Editor */}
              <div className="col-span-6 p-6">
                <div className="font-mono text-xs text-crafter-accent mb-4">{t('interface.editor')}</div>
                <div className="space-y-4">
                  <div className="h-4 w-3/4 bg-crafter-elevated/50 rounded animate-pulse" />
                  <div className="h-4 w-full bg-crafter-elevated/30 rounded animate-pulse" style={{ animationDelay: '0.1s' }} />
                  <div className="h-4 w-5/6 bg-crafter-elevated/30 rounded animate-pulse" style={{ animationDelay: '0.2s' }} />
                  <div className="h-4 w-4/5 bg-crafter-elevated/30 rounded animate-pulse" style={{ animationDelay: '0.3s' }} />
                  <div className="h-32 w-full bg-crafter-elevated/20 rounded mt-6 animate-pulse" style={{ animationDelay: '0.4s' }} />
                </div>
              </div>

              {/* Right panel - Skill set */}
              <div className="col-span-3 bg-crafter-surface/30 p-4">
                <div className="font-mono text-xs text-crafter-accent mb-4">{t('interface.skillSet')}</div>
                <div className="space-y-3">
                  {metadataFields.map((item, i) => (
                    <div
                      key={item}
                      className="p-3 rounded bg-crafter-elevated/50 border border-crafter-accent/10"
                      style={{ animationDelay: `${i * 0.15}s` }}
                    >
                      <div className="font-mono text-xs text-crafter-textDim mb-1">{item}</div>
                      <div className="h-2 w-full bg-crafter-accent/20 rounded animate-pulse" style={{ animationDelay: `${i * 0.15 + 0.5}s` }} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Floating badges */}
          <div className="absolute -left-4 top-20 px-4 py-2 bg-crafter-surface border border-crafter-accent/20 rounded-lg shadow-lg animate-float hidden lg:block">
            <span className="font-mono text-xs text-crafter-accent">{t('interface.realTimePreview')}</span>
          </div>

          <div className="absolute -right-4 bottom-32 px-4 py-2 bg-crafter-surface border border-crafter-accent/20 rounded-lg shadow-lg animate-float-delayed hidden lg:block">
            <span className="font-mono text-xs text-crafter-accent">{t('interface.syntaxHighlighting')}</span>
          </div>
        </div>
      </div>
    </section>
  )
}

export default InterfaceShowcase
